import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@siliconx/shared/ui/Button';
import { useAuth } from '../../lib/authContext';
import { qk } from '../../lib/queryKeys';
import { passportErrorMessage, setPassportPublished, usePassportPublication } from '../../lib/passportPublication';

/**
 * SIL-2409 (PR 5, O3a): when an asset listing ends without a sale (withdrawn = cancelled by the seller, or expired) and the
 * asset's passport is still public, an org admin is asked "Make the passport private again?" (Yes is the default). A completed
 * sale needs no prompt: the sale hook already made the original private and the buyer's clone starts private.
 *
 * Rendered only for ended asset listings; a member, a private passport and a taken-down passport render nothing. Choosing
 * "Yes" sends `reason: 'listing_end'`; "Keep public" is dismissed for this page view only (the toggle on the asset page
 * remains the durable control).
 */

/** Listing statuses that end a listing without a sale. */
export const LISTING_END_STATUSES: readonly string[] = ['withdrawn', 'expired'];

export function isPassportListingEnd(listing: { listingType: string; status: string; assetId: string | null }): boolean {
  return listing.listingType === 'asset' && listing.assetId != null && LISTING_END_STATUSES.includes(listing.status);
}

export function PassportListingEndPrompt({ assetId }: { assetId: string }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const pub = usePassportPublication(assetId, isAdmin);
  const [kept, setKept] = useState(false);

  const makePrivate = useMutation({
    mutationFn: () => setPassportPublished(assetId, false, 'listing_end'),
    onSuccess: async () => {
      toast.success('Passport is now private.');
      await queryClient.invalidateQueries({ queryKey: qk.assets.passportPublication(assetId) });
    },
    onError: (err: Error) => toast.error(passportErrorMessage(err)),
  });

  if (!isAdmin || kept || !pub.data || !pub.data.published || pub.data.takenDown) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted px-2 py-1"
      data-testid="passport-listing-end-prompt"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="text-xs text-foreground">Make the passport private again?</span>
      <Button size="sm" onClick={() => makePrivate.mutate()} disabled={makePrivate.isPending} data-testid="passport-listing-end-yes">
        {makePrivate.isPending ? '...' : 'Yes'}
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setKept(true)} disabled={makePrivate.isPending}>
        Keep public
      </Button>
    </div>
  );
}
